import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Paper, 
  Container, 
  Divider, 
  Chip, 
  Box,
  Alert,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  IconButton
} from '@mui/material';
import { collection, query, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { LoadingSpinner } from '../../shared/components';
import { format, isAfter, isBefore, parseISO, isToday } from 'date-fns/esm';
import NotificationsIcon from '@mui/icons-material/Notifications';
import UpdateIcon from '@mui/icons-material/Update';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';

// Notice interface
interface Notice {
  id: string;
  notice: string;
  date: Date | Timestamp;
  target_date: Date | Timestamp;
  completed: boolean;
}

// Function to convert Firestore timestamp or date string to Date object
function convertToDate(dateField: string | Timestamp | Date): Date {
  try {
    if (typeof dateField === 'string') {
      // Directly use the ISO string which is valid
      const parsedDate = new Date(dateField);
      
      // Check if the parsed date is valid
      if (isNaN(parsedDate.getTime())) {
        console.error('Failed to parse date string:', dateField);
        return new Date(); // Fallback to current date
      }
      return parsedDate;
    } else if (dateField instanceof Timestamp) {
      return dateField.toDate();
    } else if (dateField instanceof Date) {
      return dateField;
    }
  } catch (error) {
    console.error('Error converting date:', error);
  }
  return new Date(); // Fallback
}

const Notices: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setLoading(true);
        const noticesCollection = collection(db, 'notices');
        const noticesQuery = query(noticesCollection);
        const noticesSnapshot = await getDocs(noticesQuery);
        
        const noticesList: Notice[] = [];
        noticesSnapshot.forEach((doc) => {
          const data = doc.data();
          try {
            // Safely parse dates
            const parsedDate = convertToDate(data.date);
            const parsedTargetDate = convertToDate(data.target_date);
            
            noticesList.push({
              id: doc.id,
              notice: data.notice || 'No description provided',
              date: parsedDate,
              target_date: parsedTargetDate,
              completed: !!data.completed // Convert to boolean
            });
          } catch (error) {
            console.error('Error parsing notice data:', error, data);
          }
        });
        
        // Sort notices by target date (upcoming first)
        noticesList.sort((a, b) => {
          try {
            const dateA = a.target_date instanceof Timestamp ? a.target_date.toDate() : a.target_date;
            const dateB = b.target_date instanceof Timestamp ? b.target_date.toDate() : b.target_date;
            return dateA.getTime() - dateB.getTime();
          } catch (error) {
            console.error('Error sorting dates:', error);
            return 0; // Keep original order if comparison fails
          }
        });
        
        setNotices(noticesList);
      } catch (err) {
        console.error('Error fetching notices:', err);
        setError('Failed to load notices. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  // Function to get status based on dates and completion
  const getNoticeStatus = (notice: Notice) => {
    try {
      const today = new Date();
      let targetDate: Date;
      
      if (notice.target_date instanceof Timestamp) {
        targetDate = notice.target_date.toDate();
      } else if (notice.target_date instanceof Date) {
        targetDate = notice.target_date;
      } else {
        console.warn('Invalid target_date type:', typeof notice.target_date);
        return 'upcoming'; // Default status if date is invalid
      }
      
      // Verify the date is valid before comparison
      if (isNaN(targetDate.getTime())) {
        console.warn('Invalid target_date value:', targetDate);
        return 'upcoming'; // Default status if date is invalid
      }
      
      if (notice.completed) {
        return 'completed';
      } else if (isAfter(today, targetDate)) {
        return 'overdue';
      } else if (isBefore(today, targetDate) && isAfter(targetDate, new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000))) {
        return 'upcoming';
      } else {
        return 'pending';
      }
    } catch (error) {
      console.error('Error determining notice status:', error);
      return 'upcoming'; // Default status if there's an error
    }
  };

  // Function to format the date for display
  const formatDate = (date: Date | Timestamp): string => {
    try {
      let dateToFormat: Date;
      
      if (date instanceof Timestamp) {
        dateToFormat = date.toDate();
      } else if (date instanceof Date) {
        dateToFormat = date;
      } else {
        console.warn('Invalid date type:', typeof date);
        return 'Date unavailable';
      }
      
      // Verify the date is valid before formatting
      if (isNaN(dateToFormat.getTime())) {
        console.warn('Invalid date value:', dateToFormat);
        return 'Date unavailable';
      }
      
      return format(dateToFormat, 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date unavailable';
    }
  };

  // Get chip color based on status
  const getChipColor = (status: string) => {
    switch (status) {
      case 'completed':
        return theme.palette.success;
      case 'overdue':
        return theme.palette.error;
      case 'pending':
        return theme.palette.warning;
      case 'upcoming':
      default:
        return theme.palette.info;
    }
  };

  // Get icon based on status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'overdue':
        return <UpdateIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'upcoming':
      default:
        return <NotificationsIcon />;
    }
  };

  // Group notices by status
  const groupNoticesByStatus = () => {
    const grouped = {
      pending: [] as Notice[],
      upcoming: [] as Notice[],
      overdue: [] as Notice[],
      completed: [] as Notice[]
    };

    notices.forEach(notice => {
      const status = getNoticeStatus(notice);
      grouped[status as keyof typeof grouped].push(notice);
    });

    return grouped;
  };

  const groupedNotices = groupNoticesByStatus();

  // Sort state for table columns
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Notice>('target_date');

  // Create a comparison function for sorting
  const getComparator = <T extends keyof Notice>(order: 'asc' | 'desc', orderBy: T) => {
    return order === 'desc'
      ? (a: Notice, b: Notice) => {
          // Handle dates for sorting
          if (orderBy === 'date' || orderBy === 'target_date') {
            const dateA = a[orderBy] instanceof Timestamp ? (a[orderBy] as Timestamp).toDate().getTime() : (a[orderBy] as Date).getTime();
            const dateB = b[orderBy] instanceof Timestamp ? (b[orderBy] as Timestamp).toDate().getTime() : (b[orderBy] as Date).getTime();
            return dateB - dateA;
          }
          // Handle completed boolean
          if (orderBy === 'completed') {
            return (b[orderBy] ? 1 : 0) - (a[orderBy] ? 1 : 0);
          }
          // Handle string comparison
          if (typeof a[orderBy] === 'string' && typeof b[orderBy] === 'string') {
            return (b[orderBy] as string).localeCompare(a[orderBy] as string);
          }
          return 0;
        }
      : (a: Notice, b: Notice) => {
          // Handle dates for sorting
          if (orderBy === 'date' || orderBy === 'target_date') {
            const dateA = a[orderBy] instanceof Timestamp ? (a[orderBy] as Timestamp).toDate().getTime() : (a[orderBy] as Date).getTime();
            const dateB = b[orderBy] instanceof Timestamp ? (b[orderBy] as Timestamp).toDate().getTime() : (b[orderBy] as Date).getTime();
            return dateA - dateB;
          }
          // Handle completed boolean
          if (orderBy === 'completed') {
            return (a[orderBy] ? 1 : 0) - (b[orderBy] ? 1 : 0);
          }
          // Handle string comparison
          if (typeof a[orderBy] === 'string' && typeof b[orderBy] === 'string') {
            return (a[orderBy] as string).localeCompare(b[orderBy] as string);
          }
          return 0;
        };
  };

  // Handle sort request
  const handleRequestSort = (property: keyof Notice) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Utility function to apply chip styling based on notice status
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size="small" />;
      case 'overdue':
        return <Chip icon={<UpdateIcon />} label="Overdue" color="error" size="small" />;
      case 'pending':
        return <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" />;
      case 'upcoming':
      default:
        return <Chip icon={<NotificationsIcon />} label="Upcoming" color="info" size="small" />;
    }
  };

  // Get date highlight styling
  const getDateStyle = (date: Date) => {
    if (isToday(date)) {
      return { fontWeight: 'bold', color: theme.palette.warning.main };
    }
    return {};
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          App Notices & Updates
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Stay informed about upcoming app and software updates. This table displays pending and completed system changes.
        </Typography>
      </Paper>

      {loading && (
        <LoadingSpinner minHeight="100px" />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && notices.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No notices available at this time.
        </Alert>
      )}

      {!loading && !error && notices.length > 0 && (
        <TableContainer component={Paper} elevation={2}>
          <Table aria-label="notices table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'notice'}
                    direction={orderBy === 'notice' ? order : 'asc'}
                    onClick={() => handleRequestSort('notice')}
                  >
                    Notice
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'date'}
                    direction={orderBy === 'date' ? order : 'asc'}
                    onClick={() => handleRequestSort('date')}
                  >
                    Announced
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'target_date'}
                    direction={orderBy === 'target_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('target_date')}
                  >
                    Target Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'completed'}
                    direction={orderBy === 'completed' ? order : 'asc'}
                    onClick={() => handleRequestSort('completed')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notices
                .sort(getComparator(order, orderBy))
                .map((notice) => {
                  const status = getNoticeStatus(notice);
                  return (
                    <TableRow 
                      key={notice.id}
                      sx={{ 
                        '&:last-child td, &:last-child th': { border: 0 },
                        borderLeft: `4px solid ${getChipColor(status).main}`,
                        bgcolor: status === 'completed' ? 'rgba(0, 0, 0, 0.04)' : 'inherit'
                      }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="body1">{notice.notice}</Typography>
                      </TableCell>
                      <TableCell>
                        {formatDate(notice.date)}
                      </TableCell>
                      <TableCell>
                        <Typography sx={getDateStyle(notice.target_date instanceof Timestamp ? notice.target_date.toDate() : notice.target_date as Date)}>
                          {formatDate(notice.target_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(status)}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default Notices;
